<template>
  <div
    v-editable="blok"
    class="nrw relative flex w-full"
    :style="cssVars"
    :data-newsletter-widget="isFooterPlacement ? 'footer' : 'body'"
  >
    <div v-if="backgroundImage" class="absolute inset-0 overflow-hidden">
      <AssetImage :blok="backgroundImage" class="!mt-0 h-full" cover />
    </div>

    <div class="nrw__inner relative flex w-full flex-col justify-center p-4">
      <div class="nrw__box">
        <p v-if="blok.title" class="mb-2 text-2lg font-bold sm:text-2xl">
          {{ blok.title }}
        </p>
        <div
          v-if="subTitle"
          class="nrw__richtext mb-2 sm:text-lg"
          v-html="subTitle"
        />
        <div
          v-if="description"
          class="nrw__richtext mb-4"
          v-html="description"
        />

        <form novalidate @submit.prevent="handleSubmit">
          <!-- Mirrors the error slot below the input so the form stays
               vertically symmetric and nothing shifts when an error appears. -->
          <div class="nrw__reserve" aria-hidden="true" />

          <div class="relative" @focusin="openInfo" @focusout="closeInfo">
            <div class="nrw__field">
              <TextInput
                v-model="email"
                type="email"
                autocomplete="email"
                :label="
                  blok.emailLabel || $t('newsletter_subscribe.email.label')
                "
                :input-id="emailInputId"
                :appearance="showEmailError ? 'danger' : 'default'"
                :has-error-message="showEmailError"
                :readonly="fetching"
                required
              />
            </div>

            <div class="nrw__reserve">
              <p
                v-if="showEmailError"
                :id="`${emailInputId}-error`"
                class="nrw__error"
              >
                {{ $t('newsletter_subscribe.email.error') }}
              </p>
              <p v-else-if="requestFailed" class="nrw__error">
                {{ $t('newsletter_subscribe.error') }}
              </p>
            </div>

            <!-- Above the CTA, matching both brands' full newsletter forms.
                 Below it they would sit under the slide-out panel, which is
                 absolutely positioned and would swallow the first click. -->
            <div v-if="isDutchSpeaking" class="mb-2 mt-3 text-start">
              <Checkbox
                :id="`${emailInputId}-newsletter-consent`"
                :checked="newsletterConsent"
                :label="`${$t('newsletter_subscribe.newsletter')} *`"
                class="mb-4 mr-2"
                :class="{
                  nrw__invalid: showConsentError && !newsletterConsent,
                }"
                @change="newsletterConsent = !newsletterConsent"
              />

              <Checkbox
                :id="`${emailInputId}-privacy-consent`"
                :checked="privacyConsent"
                class="mb-4 mr-2"
                :class="{ nrw__invalid: showConsentError && !privacyConsent }"
                @change="privacyConsent = !privacyConsent"
              >
                <template #label>
                  <span>
                    {{ $t('newsletter_subscribe.privacyPolicy.agree') }}
                    <AgsLink
                      :to="toLocalePath('/c/sicherheit-datenschutz/')"
                      hover="underline"
                      size="sm"
                      class="underline"
                      in-paragraph
                    >
                      {{ $t('newsletter_subscribe.privacyPolicy.link') }}
                    </AgsLink>
                    {{
                      $t('newsletter_subscribe.privacyPolicy.agree_end').trim()
                    }}
                    *
                  </span>
                </template>
              </Checkbox>
            </div>

            <!-- Stays clickable while the address is invalid: pressing it is
                 what reveals the reason, rather than a dead button leaving the
                 shopper to guess. -->
            <Button
              native-type="submit"
              is-full-width
              :disabled="fetching"
              :loading="fetching"
            >
              {{ blok.buttonLabel || $t('newsletter_subscribe.submit') }}
            </Button>

            <!-- The authored text and the standing privacy notice share one
                 slot, so the widget always carries a data-protection hint.
                 Slide-out mode overlays instead of expanding, keeping the text
                 out of the widget's height budget. -->
            <div
              v-if="!blok.informationSlideOut || isInfoOpen"
              :class="
                blok.informationSlideOut
                  ? 'nrw__info-panel nrw__richtext'
                  : 'nrw__richtext mt-3 text-xs'
              "
            >
              <div v-if="hasInformationText" v-html="informationText" />
              <i18n-t
                v-else
                keypath="newsletter_subscribe.privacyPolicy.warning"
                tag="p"
              >
                <template #policy>
                  <AgsLink
                    :to="toLocalePath('/c/sicherheit-datenschutz')"
                    hover="underline"
                    class="underline"
                    in-paragraph
                  >
                    {{ $t('newsletter_subscribe.privacyPolicy.policy') }}
                  </AgsLink>
                </template>
                <template #unregister>
                  <AgsLink
                    :to="toLocalePath('/c/service/newsletter/unsubscribe')"
                    hover="underline"
                    class="underline"
                    in-paragraph
                  >
                    {{ $t('newsletter_subscribe.privacyPolicy.unregister') }}
                  </AgsLink>
                </template>
              </i18n-t>
            </div>
          </div>
        </form>
      </div>
    </div>

    <Modal
      v-if="isSuccessOpen"
      inner-class="rounded flex flex-col h-full"
      custom-class="w-full max-w-[768px]"
      size="none"
      :is-full-height="false"
      @close="isSuccessOpen = false"
    >
      <div class="flex h-full flex-col">
        <div class="shrink-0 px-4 py-5 pr-12">
          <p class="text-base font-bold sm:text-2lg">
            {{ blok.successHeadline || $t('newsletter_subscribe.success') }}
          </p>
        </div>

        <div class="flex-1 overflow-y-auto px-4">
          <div v-for="item in blok.successContent ?? []" :key="item._uid">
            <component :is="item.component" :blok="item" />
          </div>
        </div>

        <div class="shrink-0 p-4">
          <Button is-full-width @click="isSuccessOpen = false">
            {{ blok.successCtaLabel || $t('dialog.close') }}
          </Button>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref } from 'vue'
import AssetImage from './media/AssetImage.vue'
import useNewsletter from '../composables/useNewsletter'
import useCountryCheck from '../composables/useCountryCheck'
import useRouteHelpers from '../composables/useRouteHelpers'
import { FOOTER_PRE_CONTENT_KEY } from '../constants/injectionKeys'
import {
  buildNewsletterWidgetCssVars,
  isRichtextEmpty,
} from '../utils/newsletterRegisterWidget'
// `~/` intentionally resolves to the consuming app so each brand gets its own
// richtext resolver, tracking events, validators and generated types (KTD-2).
import useRichtext from '~/cms/storyblok/composables/useRichtext'
import useTrackingEvents from '~/composables/tracking/useTrackingEvents'
import { isValidEmail } from '~/utils/input'
import type { SbNewsletterRegisterWidget } from '~/cms/storyblok/types/storyblok.d.ts'

const props = defineProps<{ blok: SbNewsletterRegisterWidget }>()

const { toLocalePath } = useRouteHelpers()
const { subscribeMinimal, fetching, error } = useNewsletter()
const { trackNewsletterSubmit } = useTrackingEvents()
const { isInList: isDutchSpeaking } = useCountryCheck(['nl', 'be'])

// The footer placement only tags itself (`data-newsletter-widget="footer"`) so
// CSS can hide it whenever a page-body instance is present — see the dedup rule
// in <style>. Resolving it in CSS keeps the decision in the server-rendered
// markup, so the footer copy never flashes in and out the way the previous
// client-side v-if did on navigation (ECOM-2758, Jörg feedback).
const isFooterPlacement = inject(FOOTER_PRE_CONTENT_KEY, false)

const email = ref('')
const newsletterConsent = ref(false)
const privacyConsent = ref(false)
const hasAttemptedSubmit = ref(false)
const isInfoOpen = ref(false)
const isSuccessOpen = ref(false)

const emailInputId = computed(() => `nrw-email-${props.blok._uid}`)
const backgroundImage = computed(() => props.blok.backgroundImage?.[0])

const { content: subTitle } = useRichtext(props.blok.subTitle)
const { content: description } = useRichtext(props.blok.description)
const { content: informationText } = useRichtext(props.blok.informationText)

// An untouched richtext field still resolves to `<p></p>`, so emptiness decides
// between the authored copy and the brand's standard privacy notice.
const hasInformationText = computed(
  () => !isRichtextEmpty(informationText.value),
)

const isEmailValid = computed(() => isValidEmail(email.value))
// An empty field is only flagged once someone has tried to submit, so the error
// answers an action rather than greeting them on arrival.
const showEmailError = computed(
  () => (hasAttemptedSubmit.value || !!email.value) && !isEmailValid.value,
)

// Outside NL/BE the checkboxes are not rendered and consent is implicit, with
// the legal basis carried by the CMS `informationText` (KTD-5).
const hasConsent = computed(
  () =>
    !isDutchSpeaking.value || (newsletterConsent.value && privacyConsent.value),
)
const showConsentError = computed(
  () => hasAttemptedSubmit.value && !hasConsent.value,
)
const requestFailed = computed(() => !!error.value)

const cssVars = computed(() => buildNewsletterWidgetCssVars(props.blok))

const openInfo = () => {
  if (props.blok.informationSlideOut) {
    isInfoOpen.value = true
  }
}

// `focusin`/`focusout` bubble, so a single pair on the wrapper covers both the
// input and the CTA and the panel does not flicker as focus moves between them.
const closeInfo = (event: FocusEvent) => {
  const wrapper = event.currentTarget as HTMLElement | null
  const nextTarget = event.relatedTarget as Node | null

  if (!wrapper?.contains(nextTarget)) {
    isInfoOpen.value = false
  }
}

const handleSubmit = async () => {
  hasAttemptedSubmit.value = true

  if (fetching.value || !isEmailValid.value || !hasConsent.value) {
    return
  }

  await subscribeMinimal({
    email: email.value,
    consent: {
      newsletter: isDutchSpeaking.value ? newsletterConsent.value : true,
      privacyPolicy: isDutchSpeaking.value ? privacyConsent.value : true,
    },
  })

  // `useNewsletter` resolves either way and reports failure through `error`.
  if (error.value) {
    return
  }

  trackNewsletterSubmit(email.value)
  email.value = ''
  hasAttemptedSubmit.value = false
  isSuccessOpen.value = true
}
</script>

<style scoped>
.nrw {
  min-height: var(--nrw-height-mobile);
  background-color: var(--nrw-background);
  color: var(--nrw-foreground);
}

/* `textColor` styles the widget's own copy on the widget background. The email
   field sits on its own white box, so its text must stay dark and readable
   regardless of `textColor` — otherwise a light `textColor` renders the typed
   text and the floated label invisible (ECOM-2758, Djuan finding #2). Setting
   the colour on the wrapper (rather than forcing it onto the label) lets each
   brand's TextInput keep its own resting-label colour — the light-grey
   placeholder the full forms use — while the typed text and the floated label
   inherit the dark (ECOM-2758, Jörg feedback). The consent checkbox labels sit
   outside `.nrw__field`, so they still follow `textColor` on the widget. */
.nrw__field {
  color: #000;
}

.nrw__box {
  width: var(--nrw-box-width-mobile);
  max-width: 100%;
  margin-left: var(--nrw-box-margin-left-mobile);
  margin-right: var(--nrw-box-margin-right-mobile);
  text-align: var(--nrw-box-text-align-mobile);
}

/* Reserves room for the inline error so it can appear and disappear without
   moving anything (R9), and sets the breathing room between the field and the
   CTA the design calls for: 28px, mirrored above the field (ECOM-2758, Jörg
   feedback — was 20px). */
.nrw__reserve {
  min-height: 1.75rem;
}

/* Stays left-aligned under the field even when the layout centres everything
   else, so it reads as belonging to the input rather than to the copy. Sized
   like the full forms' inline error (text-xs) and dropped a little below the
   field so it does not crowd the border, matching the full newsletter form
   which floats it at the bottom of its group (ECOM-2758, Jörg feedback). It
   still fits inside the 28px reserve, so showing it shifts nothing. */
.nrw__error {
  color: #d00;
  font-size: 0.75rem;
  line-height: 1rem;
  text-align: start;
  padding-top: 0.5rem;
}

/* Colour only — the brands disagree on the error token (`text-red` vs
   `text-error`), so a shared blok cannot use either. */
.nrw__invalid {
  color: #d00;
}

.nrw__info-panel {
  position: absolute;
  left: 0;
  right: 0;
  z-index: 10;
  padding: 0.75rem;
  border: 1px solid currentColor;
  background-color: #fff;
  color: #000;
  font-size: 0.75rem;
  text-align: left;
}

.nrw__richtext :deep(a) {
  text-decoration: underline;
}

/* The single 640px breakpoint the ticket specifies. Resolving it in CSS rather
   than from `useDeviceType` keeps it SSR-safe and tied to the real viewport,
   and lets editors add new option values without a code change (KTD-3). */
@media (min-width: 640px) {
  .nrw {
    min-height: var(--nrw-height-desktop);
  }

  .nrw__box {
    width: var(--nrw-box-width-desktop);
    max-width: var(--nrw-width-desktop);
    margin-left: var(--nrw-box-margin-left-desktop);
    margin-right: var(--nrw-box-margin-right-desktop);
    text-align: var(--nrw-box-text-align-desktop);
  }
}
</style>

<!-- Deliberately unscoped: the de-dup selector spans the two independent widget
     instances and the `body` element, which a scoped selector cannot reach. -->
<style>
/* When a page-body widget is present anywhere on the page, hide the Footer
   `preContent` copy. Pure CSS on the SSR-rendered attributes, so the footer
   copy is hidden from the very first paint and stays hidden across client
   navigation — no flash, unlike the reactive v-if it replaces (ECOM-2758). */
body:has([data-newsletter-widget='body']) [data-newsletter-widget='footer'] {
  display: none;
}
</style>
